// Admin: the host as admission sees it (spec "Host capacity"): CPU and memory booked against
// their ceilings, what's really free against the floor, pressure, each machine's memory held on
// the host vs used inside, whether the host's guard finds it short and what it did last. Refreshes
// every 30 s.

import { Tag } from '../ui/Tag';
import { useEffect, useState } from 'react';
import { admin, type GuardEvent, type HostReport } from '../api';
import { ago } from './Shell';

const since = (unix: number) => ago(new Date(unix * 1000).toISOString());
const gb = (mb: number) => `${(mb / 1024).toFixed(mb < 10240 ? 1 : 0)} GB`;

/** A bar: how full, with an optional mark (the floor). */
function Meter({ value, max, mark, tone }: { value: number; max: number; mark?: number; tone?: 'ok' | 'warn' | 'bad' }) {
  const pct = (v: number) => `${Math.max(0, Math.min(100, (v / max) * 100))}%`;
  return (
    <div className={`host-meter ${tone ?? 'ok'}`} role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.round(value)}>
      <i style={{ width: pct(value) }} />
      {mark !== undefined && <b style={{ left: pct(mark) }} aria-hidden="true" />}
    </div>
  );
}

export function HostCard({ onPresence }: { onPresence?: (shown: boolean) => void } = {}) {
  const [r, setR] = useState<HostReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      const res = await admin.host();
      if (!alive) return;
      if (res.ok) {
        setR(res.data);
        setError(null);
      } else setError(res.error);
    };
    void load();
    const t = setInterval(() => void load(), 30_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  useEffect(() => {
    if (r) onPresence?.(!!r.host);
  }, [r, onPresence]);
  if (r && !r.host) return null; // a hub without a host (dev)
  const h = r?.host;
  const b = r?.booked;
  const tone = (used: number, max: number) => (used > max ? 'bad' : used > max * 0.85 ? 'warn' : 'ok');
  return (
    <section className="card" id="host" aria-labelledby="host-title" data-testid="admin-host">
      <div className="card-head">
        <div className="card-title">
          <h2 id="host-title">Host</h2>
          {r && (
            <Tag tone={r.full ? 'red' : 'green'} data-testid="host-room">
              {r.full ? `Full: ${r.full}` : 'Room for a new machine'}
            </Tag>
          )}
        </div>
      </div>
      {error && <p className="form-error">{error}</p>}
      {!r && !error && <div className="skeleton" aria-busy="true" aria-label="Loading the host" />}
      {h && b && r.rules && (
        <>
          <div className="usage-rows">
            <div className="usage-row" data-testid="host-cpu">
              <div className="usage-label">
                <span>CPU booked</span>
                <strong>
                  {b.cpus.toFixed(1)} of {b.cpusMax.toFixed(1)}
                </strong>
                <span className="muted small">
                  {h.cpus} CPUs × {r.rules.CPUOverbook} for Standard
                </span>
              </div>
              <Meter value={b.cpus} max={b.cpusMax} tone={tone(b.cpus, b.cpusMax)} />
            </div>
            <div className="usage-row" data-testid="host-memory-booked">
              <div className="usage-label">
                <span>Memory booked</span>
                <strong>
                  {b.memGB.toFixed(0)} of {b.memGBMax.toFixed(0)} GB
                </strong>
                <span className="muted small">
                  {gb(h.memoryMB)} × {r.rules.MemOverbook} for Standard
                </span>
              </div>
              <Meter value={b.memGB} max={b.memGBMax} tone={tone(b.memGB, b.memGBMax)} />
            </div>
            <div className="usage-row" data-testid="host-free">
              <div className="usage-label">
                <span>Free memory</span>
                <strong>{gb(h.availMB)}</strong>
                <span className="muted small">
                  lowest this hour {gb(h.minAvailMB)}; floor {gb(r.floorMB ?? 0)} after a new machine’s {gb(r.newMachine ?? 0)}
                </span>
              </div>
              <Meter
                value={h.memoryMB - Math.min(h.availMB, h.minAvailMB)}
                max={h.memoryMB}
                mark={h.memoryMB - (r.floorMB ?? 0) - (r.newMachine ?? 0)}
                tone={r.full ? 'bad' : 'ok'}
              />
            </div>
            <div className="usage-row" data-testid="host-pressure">
              <div className="usage-label">
                <span>Pressure</span>
                <strong>
                  {h.memPressure.toFixed(1)}% · {h.cpuPressure.toFixed(1)}%
                </strong>
                <span className="muted small">
                  memory · CPU, 5 min; limits {r.rules.MemPressureMax}% · {r.rules.CPUPressureMax}%
                  {h.swapTotalMB > 0 && ` · swap ${gb(h.swapTotalMB - h.swapFreeMB)} of ${gb(h.swapTotalMB)}`}
                </span>
              </div>
              <Meter value={h.cpuPressure} max={r.rules.CPUPressureMax * 2} mark={r.rules.CPUPressureMax} tone={tone(h.cpuPressure, r.rules.CPUPressureMax)} />
            </div>
          </div>

          {!!r.machines?.length && (
            <div className="table-wrap table-cards">
              <table className="table" data-testid="host-machines">
                <thead>
                  <tr>
                    <th scope="col">Machine</th>
                    <th scope="col">Held on the host</th>
                    <th scope="col">Used inside</th>
                    <th scope="col">Free inside</th>
                  </tr>
                </thead>
                <tbody>
                  {r.machines.map((m) => (
                    <tr key={m.id} data-machine-id={m.id}>
                      <td className="cell-head">
                        <code className="cell-strong">{m.id}</code> <span className="muted small">{m.tier}</span>
                        {r.blamed === m.id && (
                          <Tag tone="amber" className="host-swap">
                            growing the most
                          </Tag>
                        )}
                      </td>
                      <td className="mono" data-label="Held">
                        {gb(m.heldMB)}
                      </td>
                      <td className="mono muted" data-label="Used">
                        {m.usedMB < 0 ? '—' : gb(m.usedMB)}
                        {!!m.swapMB && m.swapMB >= 512 && <Tag tone="red" className="host-swap">swap {gb(m.swapMB)}</Tag>}
                      </td>
                      <td className="mono muted" data-label="Free">
                        {m.usedMB < 0 ? '—' : gb(m.freeMB)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="muted small host-foot">
                Looked at {r.sweptAt ? since(r.sweptAt) : 'soon'}. Machines give back the memory they free by themselves; when the host is short{r.shortSince ? ` (it is, since ${since(r.shortSince)})` : ''}, its guard asks them for their page cache back. It never limits a machine or stops anything on it; past that the host swaps.
              </p>
            </div>
          )}
          {!!r.guard?.length && (
            <ul className="host-guard host-foot muted small" data-testid="host-guard">
              {[...r.guard]
                .reverse()
                .slice(0, 6)
                .map((e) => (
                  <li key={`${e.at}-${e.id}-${e.what}`}>
                    <code>{e.id}</code> {guardStep(e)} · {since(e.at)}
                  </li>
                ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

/** One of the guard's steps, in a line. */
function guardStep(e: GuardEvent): string {
  let s = e.drill ? 'drill: ' : '';
  if (e.what === 'plug') s += `given its ${gb(e.memoryMB ?? 0)}`;
  else s += `asked for page cache: ${gb(e.cacheMB ?? 0)} → ${gb(e.cacheLeftMB ?? 0)}`;
  s += `, ${gb(e.availMB)} available on the host`;
  if (e.why) s += ` (${e.why})`;
  if (e.error) s += `: ${e.error}`;
  return s;
}
